/**
 * A table that scrolls sideways, and a badge that says so.
 *
 * Six or seven columns of figures do not fit a phone, and the answer here has
 * always been to let the table scroll inside its panel rather than squeeze the
 * numbers. That works, but nothing on screen says the columns continue past the
 * edge: a table cut off at the right looks like a table, not like a table with
 * more of itself to the right.
 *
 * So the badge appears only when there is something to scroll to, and goes once
 * you have scrolled — it is an answer to a question, not decoration. It is
 * `aria-hidden` and takes no pointer events: the scrolling itself is what does
 * the work, and this only mentions it.
 */
import m from 'mithril';

interface ScrollHintAttrs {
    /** Classes for the wrapper the badge is positioned against. */
    class?: string;
    /** Classes for the scrolling box itself — `.table-scroll` and friends. */
    boxClass?: string;
}

interface ScrollHintState {
    box?: HTMLElement;
    observer?: ResizeObserver;
    /** There is more table than box. */
    scrollable: boolean;
    /** They have already gone looking, so the badge has said its piece. */
    scrolled: boolean;
    measure: () => void;
}

export function ScrollHint(): m.Component<ScrollHintAttrs, ScrollHintState> {
    return {
        oninit: ({ state }) => {
            state.scrollable = false;
            state.scrolled = false;
            state.measure = () => {
                const box = state.box;
                const table = box?.firstElementChild as HTMLElement;
                if (!box || !table) {
                    return;
                }
                // The table's own width, not the box's scrollWidth: the column
                // hints are absolutely positioned inside the box and hang a
                // little past the last one, which counts as scrollable width
                // even while they are hidden. A table that fits would offer to
                // swipe you 23px of nothing.
                const scrollable = table.offsetWidth - box.clientWidth > 8;
                const scrolled = box.scrollLeft > 8;
                if (
                    scrollable !== state.scrollable ||
                    scrolled !== state.scrolled
                ) {
                    state.scrollable = scrollable;
                    state.scrolled = scrolled;
                    // Neither the scroll listener nor the observer below is
                    // bound by mithril, so nothing redraws for them.
                    m.redraw();
                }
            };
        },
        oncreate: ({ dom, state }) => {
            state.box = dom.querySelector('.scroller__box') as HTMLElement;
            // Passive, and it only redraws when the answer changes: a redraw
            // per scroll event would re-diff the whole page as you drag.
            state.box.addEventListener('scroll', state.measure, {
                passive: true,
            });
            // domino, under test, has neither ResizeObserver nor a layout to
            // measure — the badge simply never appears there.
            if (typeof ResizeObserver === 'undefined') {
                return;
            }
            state.observer = new ResizeObserver(state.measure);
            state.observer.observe(state.box);
        },
        // The columns can change width without the box doing, so measure on
        // every update as well as on a resize.
        onupdate: ({ state }) => state.measure(),
        onremove: ({ state }) => {
            state.box?.removeEventListener('scroll', state.measure);
            state.observer?.disconnect();
        },
        view: ({ attrs, children, state }) =>
            m('.scroller', { class: attrs.class }, [
                m(
                    '.scroller__box.table-scroll-x',
                    { class: attrs.boxClass },
                    children,
                ),
                state.scrollable
                    ? m(
                          '.scroller__hint',
                          {
                              class: state.scrolled
                                  ? 'scroller__hint--done'
                                  : '',
                              'aria-hidden': 'true',
                          },
                          '‹ swipe ›',
                      )
                    : null,
            ]),
    };
}
